import type { Entry } from "contentful";
import type { RefObject } from "react";
import { Suspense, useEffect, useRef, useState } from "react";
import { Await, Link, PrefetchPageLinks, useNavigation } from "react-router";

import { AvatarIcon, HamburgerMenuIcon } from "@radix-ui/react-icons";

import { NavigationMenuLink } from "~/components/ui/navigation-menu";
import type { TypeTextBlockSkeleton } from "~/contentful/compiled";
import { useURL } from "~/contexts";
import type { Customer } from "~/lib/app.types";
import type { SerializeFrom } from "~/lib/types";
import { BasketIcon } from "~/routes/resources/basket";

import { CoutrySelector } from "../country-selector";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { Button } from "../ui/button";
import { Dialog, DialogContent } from "../ui/dialog";
import { Logo } from "../ui/icons/logo";
import { LocationIcon } from "../ui/location-icon";
import { MainMenu, MainMenuMobile } from "./meganav-main-menu";
import { MobileMenu } from "./meganav-mobile-menu";
import * as Nav from "./meganav-nav";
import { MeganavProgress } from "./meganav-progress";
import { MeganavSearchBar } from "./meganav-search-bar";
import { MeganavSearchBarSuggestions } from "./meganav-search-suggestions";
import MeganavSubmenu from "./meganav-submenu";
import { SearchProvider } from "./search-context";
import StickyHeader from "./sticky-header";
import TopBarMessagesSlider from "./top-bar-messages-slider";

/**
 * NB: check \@see section below
 *
 * ## Meganav
 *
 * This component is the main navigation for the site.
 * It has a background that is revealed when the user scrolls past the ManuBoundy component.
 * ##Usage:
 * ```tsx
 * const boundryRef = useRef<HTMLDivElement>(null);
 * ...
 * <Meganav boundryRef={boundryRef} />
 * ...
 * ...
 * // some content
 * ...
 * ...
 * ...
 * <MenuBoundry ref={boundryRef} />
 * ```
 * --------------------
 * ## Routes
 * In routes file you can specify the variant of the meganav
 *
 * ```tsx
 *  export const handle: AppHandle = {
 *   meganav: {
 *    variant: "light",
 *  //or
 *   variant: (data:SerializeFrom<typeof loader>) =>{
 *      return data.theme === "light" ? "light" : "dark"
 *   }
 *  },
 * };
 * ```
 * --------------------
 * ## Types
 * @see app/lib/utils-types/handle.types.ts
 * --------------------
 * @see https://remix.run/docs/en/main/route/handle (handle)
 * @see https://remix.run/docs/en/main/hooks/use-matches (matches)
 * @see https://sergiodxa.com/articles/bubble-up-data-on-remix-routes
 */

export const Meganav = ({
  boundryRef,
  isLoggedIn,
  user,
  messages,
}: {
  boundryRef: RefObject<HTMLElement>;
  isLoggedIn: boolean;
  user: Customer | undefined;
  messages?: Promise<
    | SerializeFrom<Entry<
        TypeTextBlockSkeleton,
        "WITHOUT_UNRESOLVABLE_LINKS",
        string
      > | null>
    | undefined
  >;
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const url = useURL();
  return (
    /* The whole nav content container */
    <Nav.Root boundryRef={boundryRef}>
      <PrefetchPageLinks page={url("/shop/anything")} />
      {/* Top Bar Heading */}
      <Suspense fallback={null}>
        <Await resolve={messages}>
          {messages => <TopBarMessagesSlider data={messages} />}
        </Await>
      </Suspense>
      <StickyHeader>
        <Nav.Content ref={menuRef}>
          {/* Header */}
          <Nav.Header className="w-auto items-center justify-between pl-10 2xl:pl-24 sm-max:h-[68px] sm-max:w-full sm-max:flex-row sm-max:bg-transparent sm-max:px-4 sm-max:py-5">
            <Nav.NavBox position="left">
              <LeftSideContent />
            </Nav.NavBox>
            <NavigationMenuLink
              to={url("/")}
              aria-label="Go to homepage"
              className="top-0 flex-1 sm-max:pl-5"
            >
              <div className="block sm-max:hidden">
                <Logo width="169" height="60" />
              </div>
              <div className="hidden sm-max:block">
                <Logo width="90" height="15" />
              </div>
            </NavigationMenuLink>
            <Nav.NavBox
              position="right"
              className="pr-8 sm:pl-0 lg:pl-0 2xl:pr-24 sm-max:pr-4"
            >
              <RightSideContent isLoggedIn={isLoggedIn} user={user} />
            </Nav.NavBox>
          </Nav.Header>
          {/* Menu Content */}
          <Nav.MenuContent className="pl-[33px] 2xl:pl-[88px] sm-max:hidden">
            <MainMenu />
            <MeganavProgress />
          </Nav.MenuContent>
          <Nav.MenuContent className="my-5 hidden pl-4 pr-6 sm-max:hidden">
            <MainMenuMobile />
          </Nav.MenuContent>
          {/* Additional components of the navigation  for left/right content */}
        </Nav.Content>
      </StickyHeader>
    </Nav.Root>
  );
};

export const LeftSideContent = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const key = useNavigation().location?.key;
  const url = useURL();
  useEffect(() => {
    setMenuOpen(false);
  }, [key]);
  return (
    <div className="relative">
      <HamburgerMenuIcon
        height={24}
        width={24}
        className="hidden sm-max:block"
        onClick={() => setMenuOpen(true)}
      />
      <Dialog open={menuOpen} onOpenChange={() => setMenuOpen(false)}>
        <DialogContent className="left-0 block h-screen translate-x-0 overflow-y-auto bg-panel-translucent p-0">
          <div className="relative bg-neutral-2 pb-4 pt-9">
            <div className="absolute left-7 top-5">
              <CoutrySelector />
            </div>
            <div className="absolute left-24 top-5">
              <Link to={"/store-locator"}>
                <LocationIcon width="18" height="18" className="mb-1" />
              </Link>
            </div>
            <MobileMenu key={key} />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const getInitials = (name: string) => {
  return name.trim()[0]?.toUpperCase() || "";
};

export const RightSideContent = ({
  isLoggedIn,
  user,
}: {
  isLoggedIn: boolean;
  user: Customer | undefined;
}) => {
  const url = useURL();
  return (
    <div className="flex items-center justify-center gap-5">
      <SearchProvider>
        <Nav.Search>
          <MeganavSearchBar
            suggestions={text => (
              <MeganavSearchBarSuggestions q={text} isSearchOverlay={true} />
            )}
          />
        </Nav.Search>
      </SearchProvider>
      <div className="pt-1 sm-max:pt-0">
        <BasketIcon />
      </div>
      <Button
        as={Link}
        aria-label="Go to my account"
        to={url("/account")}
        prefetch="intent"
        variant="linkInline"
        className="hover:no-underline"
        data-testid="my-account-button"
      >
        {isLoggedIn ? (
          <Avatar className="inline-block">
            <AvatarFallback className="bg-accountImage text-3xs font-bold text-white">
              {getInitials(user?.firstName || "")}
            </AvatarFallback>
          </Avatar>
        ) : (
          <AvatarIcon className="h-[24px] w-[24px]" />
        )}
      </Button>
      <MeganavSubmenu />
    </div>
  );
};
